import styles from './brand.module.css';

/**
 * The Progress Strip. Brand guidelines section 03, the signature device.
 *
 * Five bars, one per update we released, whose heights are how long people
 * stayed in the game on that update. Newest rightmost and always Volt. An
 * update people stay in draws a taller bar; one they quit early draws a shorter
 * one, and nobody has to write a sentence about it.
 *
 * **The one rule: never draw a strip that does not represent real data.**
 *
 * *"Fabricating a rising staircase for a game that has not earned it turns the
 * platform's most honest device into its biggest lie, and it would be
 * noticed."*
 *
 * That rule is enforced here by the types rather than by a comment. There are
 * exactly four components, and none of them accepts an arbitrary list of bar
 * heights:
 *
 *   - `VersionStrip` takes updates with their hours and derives the heights.
 *     It cannot draw a shape that the hours did not produce.
 *   - `DownloadStrip` takes a completed count. Equal bars, no score implied.
 *   - `LogoStrip` is the fixed identity mark, at the proportions the
 *     guidelines specify. It is a logo, not a chart, and it is the only one
 *     with hard-coded heights.
 *   - `Loader` is that same mark with a light running through it. It takes no
 *     data at all, and it animates colour rather than height precisely so that
 *     a wait cannot draw a rising staircase nobody earned. `PageLoader` is the
 *     same component given the whole window, for the waits that happen before
 *     there is a page to put one inside.
 *
 * There is deliberately no `<Strip heights={[30, 50, 70]} />`. A component
 * that took heights directly is the one somebody would reach for to decorate a
 * marketing page, and the rule would be broken within a week.
 */

/**
 * The identity lockup's bar heights. Fixed, from the guidelines.
 *
 * Exported because the mark is now drawn in three places — this component,
 * `tools/brand-mark.mjs` for the raster and favicon set, and the ident film in
 * `apps/reel`, which animates each bar separately and so cannot use
 * `LogoStrip` as a black box. Exporting the numbers is what stops the third
 * one from becoming a fourth opinion about what the logo looks like.
 *
 * It is the heights only. There is still no component that accepts arbitrary
 * bar heights, and there must not be — see the rule above.
 */
export const LOGO_BARS = [30, 48, 66, 82, 100] as const;

export type StripSize = 'tiny' | 'sm' | 'md' | 'lg' | 'display';

/* CSS module members are typed as possibly-undefined, so an unmatched class
   name degrades to no class rather than the literal string "undefined". */
const SIZE_CLASS: Record<StripSize, string> = {
  tiny: styles.stripTiny ?? '',
  sm: styles.stripSm ?? '',
  md: styles.stripMd ?? '',
  lg: styles.stripLg ?? '',
  display: styles.stripDisplay ?? '',
};

/**
 * The mark. Four or five bars at fixed heights, top bar always Volt.
 *
 * Decorative in the sense that a logo is decorative — it stands for the studio,
 * not for any title's hours. The heights never change, which is what keeps it a
 * mark rather than a chart somebody might read.
 *
 * Four bars below 96px and never fewer. Section 02: under that size the first
 * two sit so close together that only the last one reads, so the name comes off
 * and the strip loses a bar rather than the lockup being set at a size nothing
 * in it is legible at.
 */
export function LogoStrip({
  size = 'sm',
  bars = 5,
  onDark = false,
}: {
  size?: StripSize;
  bars?: 4 | 5;
  onDark?: boolean;
}): React.ReactElement {
  const heights = bars === 4 ? [30, 53, 76, 100] : [...LOGO_BARS];

  return (
    <span
      className={`${styles.strip} ${SIZE_CLASS[size]}`}
      aria-hidden="true"
      data-on-dark={onDark ? '' : undefined}
    >
      {heights.map((height, index) => (
        <i
          key={height}
          style={{ height: `${height}%` }}
          /*
            Two dim, then the accent, then Volt on top.

            Section 02 draws it and says why: *"the top bar is always Volt — its
            only spot in the logo"*. The two under it are the accent, and the
            two at the foot are the ground's own neutral. The count of dim bars
            is two in both the five-bar and the four-bar lockup, which is why
            this reads from the front rather than from the back.
          */
          data-bar={index < 2 ? 'dim' : index === heights.length - 1 ? 'current' : 'mark'}
        />
      ))}
    </span>
  );
}

/*
 * The running strip's own size classes, rather than `.stripSm` and friends.
 *
 * Those carry `gb-bar-grow` — the one-shot assemble the strip does when a page
 * paints — and a bar cannot run two animations on the same property. Sharing
 * them would have meant unpicking that with `nth-child` selectors of matching
 * specificity, which is a thing to get wrong every time either animation is
 * touched. CSS module members are typed as possibly-undefined, as above.
 */
const LOADER_SIZE: Record<Exclude<StripSize, 'tiny' | 'display'>, string> = {
  sm: styles.loaderSm ?? '',
  md: styles.loaderMd ?? '',
  lg: styles.loaderLg ?? '',
};

/**
 * Waiting, drawn as the mark.
 *
 * **Every surface used to wait by printing `Loading…` into the empty state** —
 * the same dashed box a table with no rows gets — so *we are still asking* and
 * *there is nothing here* were the same picture for as long as the request
 * took. On a games platform, of all things.
 *
 * This is the logo with a light running up it, and then a beat, and then again.
 * The heights never move: section 03's one rule is that a strip must never draw
 * a shape its data did not produce, and a loader that animated bar heights
 * would hand every game a rising staircase for free while it waited. What moves
 * is the colour and a three-pixel lift, so what is on screen stays exactly the
 * identity mark.
 *
 * **It waits in the colours of whoever is reading.** The bars take
 * `--gb-accent`, which is Build on the store, Hot on a creator's board and
 * Solar on a working surface — so one component is right on all three and
 * nothing has to pass it a channel. `space` is only for a surface that has not
 * declared one, and it paints that surface's ground as well as its accent,
 * because `[data-gb-space]` carries both.
 *
 * The label is a real sentence rather than decoration: `role="status"` means a
 * screen reader hears it when it appears, and `Reading Steam` tells somebody
 * which of the two requests on the page they are waiting for.
 */
export function Loader({
  label = 'Loading',
  size = 'md',
  block = false,
  space,
}: {
  /** What is being waited for. Announced, so write it for a person. */
  label?: string;
  size?: Exclude<StripSize, 'tiny' | 'display'>;
  /** Centred, with room around it: a page waiting rather than a control. */
  block?: boolean;
  /**
   * Whose colours, where the surface has not already said.
   *
   * Almost nothing passes this. Every `Page` and every `AuthScreen` already
   * carries `data-gb-space`, and the accent resolves from the nearest one — so
   * setting it again here would only be a second opinion about a question
   * already answered.
   */
  space?: string;
}): React.ReactElement {
  const mark = (
    <span className={styles.loader} role="status" {...(space ? { 'data-gb-space': space } : {})}>
      <span className={`${styles.loaderStrip} ${LOADER_SIZE[size]}`} aria-hidden="true">
        {LOGO_BARS.map((height) => (
          <i key={height} style={{ height: `${height}%` }} />
        ))}
      </span>
      <span className={styles.loaderLabel}>{label}</span>
    </span>
  );

  if (!block) return mark;
  return <div className={styles.loaderBlock}>{mark}</div>;
}

/**
 * Waiting when there is nothing else on the screen yet.
 *
 * `Loader` waits *inside* a page — beside a button, or in the content area
 * under a heading that is already true. This is the other situation, and the
 * React app had no answer for it at all: the session check that runs on every
 * page load rendered an empty fragment, so the first thing anybody saw of this
 * product, every single time, was an empty rectangle for the length of a
 * network request. The same fragment covered the two redirects — to sign in,
 * and to the storefront for a path this origin does not serve — where the wait
 * is a whole browser navigation.
 *
 * **`space` is required rather than optional**, which is the one place this
 * differs from `Loader`. Everywhere else the accent resolves from the nearest
 * `[data-gb-space]` ancestor, and there is not one here: this renders *instead*
 * of `Page`, above it, or before it exists. Without the attribute the ground
 * would be the `:root` default — the store's — which is how a studio ends up
 * watching a warm orange loader on the way into its own blue portal.
 *
 * `lg`, because it is alone on the screen and the `md` mark centred in a
 * window reads as something that failed to finish loading rather than as the
 * thing that is loading.
 */
export function PageLoader({
  label,
  space,
  over = false,
}: {
  /** What is being waited for. Announced, so write it for a person. */
  label?: string;
  /** Whose colours. Required: there is no surface above this to inherit from. */
  space?: string;
  /**
   * Over the page rather than instead of it.
   *
   * For a navigation, where the page being left is still perfectly good and
   * should stay until the next one is ready. A first load has nothing to cover
   * and takes the default.
   */
  over?: boolean;
}): React.ReactElement {
  return (
    <div
      className={`${styles.loaderPage ?? ''} ${over ? (styles.loaderOver ?? '') : ''}`}
      data-gb-space={space}
    >
      <Loader size="lg" {...(label ? { label } : {})} />
    </div>
  );
}

export interface PlayedUpdate {
  /** The update this bar is. `2.6`, `v1.4`. */
  version: string;
  /** How long people stayed in this update, in hours. The bar's height. */
  hours: number;
  /** How many people that is measured over. A figure without one is not shown. */
  players: number;
}

/**
 * Update history, drawn from hours played.
 *
 * **The bar is hours, not a score.** Guidelines v5.0 settles what the device
 * measures: *"One bar is one update we released. The height is how long people
 * played that update, measured in hours."* It used to be the percentage of
 * reviews that were positive, which is a rating — and *"we do not rate our own
 * games"* is one of the six things to remember. Ratings are somebody's opinion
 * collected; hours are a thing that happened.
 *
 * Heights are that measurement scaled to the tallest bar in the set, so the
 * shape is the shape of the numbers. There is no smoothing, no minimum delta
 * and no filtering: an update people stayed in for less time draws a shorter
 * bar and ships that way. Section 03 — *"Going down. Draw this one exactly like
 * the others."*
 *
 * A single update draws a single bar. A brand new game is not a staircase.
 */
export function VersionStrip({
  versions,
  size = 'md',
  onDark = false,
}: {
  versions: readonly PlayedUpdate[];
  size?: StripSize;
  onDark?: boolean;
}): React.ReactElement | null {
  if (versions.length === 0) return null;

  /*
   * The tallest bar is the tallest number, and everything else is a share of
   * it. An absolute scale would mean a game people play for two hours drew five
   * slivers and a game people play for forty drew five full-height bars, and
   * the strip is a picture of one game's own history rather than a comparison
   * between games.
   */
  const tallest = Math.max(...versions.map((entry) => entry.hours));

  const label = versions
    .map((entry) => `${entry.version}: ${entry.hours} hours from ${entry.players} players`)
    .join(', ');

  return (
    <span
      className={`${styles.strip} ${SIZE_CLASS[size]}`}
      role="img"
      aria-label={`Hours played by update. ${label}.`}
    >
      {versions.map((entry, index) => (
        <i
          key={entry.version}
          /*
           * A short update still draws a visible bar rather than nothing, or
           * the update people liked least disappears out of its own history —
           * which is the flattering version of the chart and the one section 03
           * forbids.
           */
          style={{ height: `${Math.max(4, tallest === 0 ? 0 : (entry.hours / tallest) * 100)}%` }}
          data-bar={index === versions.length - 1 ? 'current' : 'past'}
          data-on-dark={onDark ? '' : undefined}
        />
      ))}
    </span>
  );
}

/**
 * Download and install progress. Equal bars filling left to right.
 *
 * The fourth shape in section 03, and the only one where equal heights are
 * correct: nothing here is a measurement of hours, so nothing should look like
 * one. *"Only for things with an end, like a download."*
 *
 * One bar is Volt and it is the one being worked on — the guidelines draw the
 * loading strip with a single lime block among neutral ones, travelling, rather
 * than a lime run growing from the left. What is happening right now is one
 * chunk, and Volt means exactly that.
 */
export function DownloadStrip({
  total,
  completed,
  size = 'sm',
}: {
  total: number;
  completed: number;
  size?: StripSize;
}): React.ReactElement {
  const bars = Array.from({ length: Math.max(1, total) }, (_, index) => index);

  return (
    <span
      className={`${styles.strip} ${SIZE_CLASS[size]}`}
      role="img"
      aria-label={`${completed} of ${total} complete.`}
    >
      {bars.map((index) => (
        <i
          key={index}
          style={{ height: '100%' }}
          data-bar={index === completed - 1 ? 'current' : index < completed - 1 ? 'past' : 'dim'}
        />
      ))}
    </span>
  );
}
